import { IStairCalculationResult, IStepGeometry } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

const PAD_LEFT: number = 38;
const PAD_RIGHT: number = 24;
const PAD_TOP: number = 28;
const PAD_BOTTOM: number = 24;
const MIN_INNER: number = 80;
const FIT_FACTOR: number = 0.85;
const WEDGE_FILL_ALPHA: number = 0.45;

// Проекторы объявлены свойствами-функциями, а не методами: их разбирают
// деструктуризацией (`const { wx, wy } = projector`), и отвязанный от объекта
// метод потерял бы `this`.
interface IProjector {
    readonly wx: (x: number) => number;
    readonly wz: (z: number) => number;
}

function wedgePoints(corners: NonNullable<IStepGeometry['winderCorners']>, projector: IProjector): IPoint[] {
    const points: IPoint[] = [];
    points.push([projector.wx(corners.innerBack.x), projector.wz(corners.innerBack.z)]);
    points.push([projector.wx(corners.outerBack.x), projector.wz(corners.outerBack.z)]);
    if (corners.outerMidPoints) {
        for (const point of corners.outerMidPoints) {
            points.push([projector.wx(point.x), projector.wz(point.z)]);
        }
    }
    points.push([projector.wx(corners.outerFront.x), projector.wz(corners.outerFront.z)]);
    if (corners.innerMidPoints) {
        for (const point of corners.innerMidPoints) {
            points.push([projector.wx(point.x), projector.wz(point.z)]);
        }
    }
    points.push([projector.wx(corners.innerFront.x), projector.wz(corners.innerFront.z)]);
    return points;
}

/**
 * World-space bounding box over every corner of all winder wedges, or null when no
 * finite corners were found. Extracted from `projectWinderCombined` to keep its
 * complexity low.
 */
function computeWinderBounds(winders: IStepGeometry[]): { xMin: number; xMax: number; zMin: number; zMax: number } | null {
    let xMin: number = Infinity;
    let xMax: number = -Infinity;
    let zMin: number = Infinity;
    let zMax: number = -Infinity;
    for (const step of winders) {
        const corners: NonNullable<IStepGeometry['winderCorners']> | undefined = step.winderCorners;
        if (!corners) {
            continue;
        }
        const pts: { x: number; z: number }[] = [corners.innerBack, corners.innerFront, corners.outerBack, corners.outerFront];
        if (corners.outerMidPoints) {
            pts.push(...corners.outerMidPoints);
        }
        if (corners.innerMidPoints) {
            pts.push(...corners.innerMidPoints);
        }
        for (const point of pts) {
            xMin = Math.min(xMin, point.x);
            xMax = Math.max(xMax, point.x);
            zMin = Math.min(zMin, point.z);
            zMax = Math.max(zMax, point.z);
        }
    }
    if (!isFinite(xMin) || !isFinite(zMin)) {
        return null;
    }
    return { xMin, xMax, zMin, zMax };
}

/**
 * Winder combined scheme (схема забежных ступеней) — a top-down overlay of all
 * winder treads in the corner, each a semi-transparent wedge with its inner-back→
 * outer-front hypotenuse, framed and dimensioned. Pure projection in pane-local
 * pixels. 1:1 port of PlanViewComponent#drawWinderCombined.
 */
export function projectWinderCombined(result: IStairCalculationResult, pane: IPaneRect, measurer: ITextMeasurer): IProjectionOutput {
    const winders: IStepGeometry[] = result.geometry.steps.filter((s: IStepGeometry) => s.isWinder && !!s.winderCorners);
    if (winders.length === 0) {
        return emptyProjection();
    }

    const bounds: { xMin: number; xMax: number; zMin: number; zMax: number } | null = computeWinderBounds(winders);
    if (!bounds) {
        return emptyProjection();
    }
    const { xMin, xMax, zMin, zMax }: { xMin: number; xMax: number; zMin: number; zMax: number } = bounds;
    const worldW: number = xMax - xMin;
    const worldH: number = zMax - zMin;
    if (worldW <= 0 || worldH <= 0) {
        return emptyProjection();
    }

    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerH: number = pane.h - PAD_TOP - PAD_BOTTOM;
    if (innerW < MIN_INNER || innerH < MIN_INNER) {
        return emptyProjection();
    }

    const scale: number = Math.min(innerW / worldW, innerH / worldH) * FIT_FACTOR;
    const drawW: number = worldW * scale;
    const drawH: number = worldH * scale;
    const ox: number = PAD_LEFT + (innerW - drawW) / 2;
    const oy: number = PAD_TOP + (innerH - drawH) / 2;
    const projector: IProjector = {
        wx: (x: number): number => ox + (x - xMin) * scale,
        wz: (z: number): number => oy + (zMax - z) * scale, // flip Z so up = forward in plan
    };
    const { wx, wz }: IProjector = projector;

    const primitives: IPlanPrimitive[] = [];

    // Outer frame.
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [wx(xMin), wz(zMax)],
            [wx(xMin) + drawW, wz(zMax)],
            [wx(xMin) + drawW, wz(zMax) + drawH],
            [wx(xMin), wz(zMax) + drawH],
        ],
        closed: true,
        stroke: EColorRef.Ink3,
        width: 0.8,
    });

    // Each winder wedge (semi-transparent fill so overlaps show) + its hypotenuse.
    for (const step of winders) {
        const corners: NonNullable<IStepGeometry['winderCorners']> | undefined = step.winderCorners;
        if (!corners) {
            continue;
        }
        const wedge: IPolyPrimitive = {
            kind: EPrimitiveKind.Poly,
            points: wedgePoints(corners, projector),
            closed: true,
            fill: EColorRef.Winder,
            fillAlpha: WEDGE_FILL_ALPHA,
            stroke: EColorRef.StepStroke,
            width: 0.8,
        };
        primitives.push(wedge);
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [wx(corners.innerBack.x), wz(corners.innerBack.z)],
                [wx(corners.outerFront.x), wz(corners.outerFront.z)],
            ],
            closed: false,
            stroke: EColorRef.Ink3,
            width: 0.5,
            dash: [3, 2],
        });
    }

    // Overall corner dimensions.
    primitives.push(horizontalDim(wx(xMin), wz(zMax) - 14, wx(xMax), String(Math.round(worldW)), measurer));
    primitives.push(verticalDim(wx(xMin) - 14, wz(zMax), wz(zMin), String(Math.round(worldH)), measurer));

    // Millimetre origin: the corner's world (x = 0, z = 0); Z is already flipped so it grows up the screen.
    return { primitives, pxPerMm: scale, origin: { x: ox - xMin * scale, y: oy + zMax * scale } };
}
